import { DualListBox, FormItem, FormLabel, Select } from '@/components/wm';
import { getOptionsFromJson, getStoreResource } from '@/utils';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

export function SelectIndicators ({ data, onChangeField, onCompleted }) {
    const formSchema = z.object({
        indicatorIds: z.array(z.any()).min(1, i18next.t('alarms.no_indicators_selected').capitalize()),
        indicatorKind: z.string().min(1, 'Kind is required')
    });

    const form = useForm({
        resolver: zodResolver(formSchema),
        defaultValues: {
            indicatorIds: data.indicatorIds || [],
            indicatorKind: data.indicatorKind || ''
        },
        mode: 'onChange'
    });

    // We watch the selected IDs so we can keep the right side in sync with the form state
    const selectedIds = form.watch('indicatorIds');
    const selectedKind = form.watch('indicatorKind');

    const indicatorKindOptions = useMemo(() => {
        const indicatorKinds  = getStoreResource('indicatorKinds') || [];

        const validOptions = indicatorKinds.filter(kind => {
            if (data.type === 'cumulative' && kind.cumulative === false) {
                return false;
            }

            const indicators = getStoreResource('indicators', null, { kind_id: kind.id }) || [];

            return indicators.some(indicator => {
                const alarms = getStoreResource('alarms', null, { indicator_id: indicator.id }) || [];
                return alarms.length > 0;
            });
        });

        return validOptions.map(item => ({
            label: i18next.t(`indicatorKinds.${item.slug}`).capitalize() || 'Unknown',
            value: String(item.id)
        }));
    }, []);

    const indicatorOptions = useMemo(() => {
        const rawData  = getStoreResource('indicators', null, { kind_id: selectedKind }) || [];
        return getOptionsFromJson(rawData);
    }, [selectedKind]);

    const errorMessage = useMemo(() => {
        if (selectedKind && indicatorOptions.length === 0) {
            return i18next.t('alarms.no_indicators_found').capitalize();
        }
        return form.formState.errors.indicatorIds?.message;
    }, [form.formState.errors.indicatorIds, selectedKind, indicatorOptions.length]);

    const handleChange = newIds => {
        // Update Hook Form (triggers Zod validation)
        form.setValue('indicatorIds', newIds, { shouldValidate: true });

        // Update parent data values
        if (onChangeField) {
            onChangeField('indicatorIds')(newIds);
        }
    };

    const handleKindChange = value => {
        form.setValue('indicatorKind', value, { shouldValidate: true });

        if (onChangeField) {
            onChangeField('indicatorKind')(value);
        }
    };

    useEffect(() => {
        onCompleted(form.formState.isValid);
    }, [form.formState.isValid, onCompleted]);

    return (
        <div className="tw-w-full">
            <div className="tw-max-w-md">
                <FormItem>
                    <FormLabel>{i18next.t('glossary.indicator_kind').capitalize()}</FormLabel>
                    <Select
                        className="!tw-mb-6"
                        disabled={selectedIds.length > 0}
                        onChange={value => handleKindChange(value)}
                        options={indicatorKindOptions}
                        placeholder={i18next.t('ui.please_select_option').capitalize()}
                        value={selectedKind}
                    />
                </FormItem>
            </div>

            <DualListBox
                error={errorMessage}
                onChange={handleChange}
                options={indicatorOptions}
                searchPlaceholder={i18next.t('alarms.filter_indicators').capitalize()}
                selectedIds={selectedIds}
                title={i18next.t('indicators.select_indicators').capitalize()}
            />
        </div>
    );
}
